import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import moment from "moment";
import { useMemo } from "react";
import { formatDatamartFilters } from "utils/formatV2DatamartFilters";
import { mergeTrendData } from "utils/mergeTrendData";
import { extendDSPMetrics } from "./utils";
import { useAppSelector } from "redux/hooks";
import useDSPSellerInfo from "./hooks/useDSPSellerInfo";

const useDSPLineItemData = (
  dimensions = "line_item_id",
  includeMetadata = true,
  includeStats = true,
  filters,
  includeTargetData = false,
) => {
  const { start, end, preStart, preEnd } = useDates();
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { dspAdvertiser } = useDSPSellerInfo();

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");
  const formattedFilters = formatDatamartFilters({ ...filters, dsp_seller_info_id: dspAdvertiser?.id });
  const { data: dspLineItemEntityData, isLoading: isDSPLineItemEntityDataLoading } = useFetch(
    ["dspLineItemData", dimensions, formattedFilters],
    "/api/data_report_v2/dsplineitem/entities/",
    {
      filters: formattedFilters,
    },
    {
      default: [],
      select: (res) => {
        return res.data?.map((lineItem) => ({
          ...lineItem,
          href: `/user/dsp/${dspAdvertiser?.id}/lineitems/${lineItem.ad_group_id}`,
        }));
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeMetadata,
    },
  );

  const { data: dspLineTargetData, isLoading: isDSPTargetDataLoading } = useFetch(
    ["dspLineItemTargetData", dspLineItemEntityData?.[0]?.id],
    "/dsp/target/",
    {
      line_item: dspLineItemEntityData?.[0]?.id,
      marketplace: marketPlace,
      limit: 5000,
    },
    {
      default: [],
      select: (res) => {
        return res.data.results;
      },
      staleTime: 5 * 60 * 1000,
      enabled: includeTargetData && !!dspLineItemEntityData?.[0]?.id,
    },
  );

  const { data: dspLineItemStatsData, isLoading: isDSPLineItemStatsDataLoading } = useFetch(
    ["dspLineItemStatsData", startDate, endDate, dimensions, formattedFilters],
    "/api/data_report_v2/dsplineitem/stats/",
    {
      dimensions,
      filters: formatDatamartFilters({
        ...filters,
        date__gte: startDate,
        date__lte: endDate,
        dsp_seller_info_id: dspAdvertiser?.id,
      }),
    },
    {
      default: [],
      select: (res) => {
        return extendDSPMetrics(res.data);
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeStats,
    },
  );

  const { data: prevPeriodDSPLineItemStatsData, isLoading: isPrevPeriodDSPLineItemStatsDataLoading } = useFetch(
    ["prevPeriodDSPLineItemStatsData", preStartDate, preEndDate, dimensions, formattedFilters],
    "/api/data_report_v2/dsplineitem/stats/",
    {
      dimensions,
      filters: formatDatamartFilters({
        ...filters,
        date__gte: preStartDate,
        date__lte: preEndDate,
        dsp_seller_info_id: dspAdvertiser?.id,
      }),
    },
    {
      default: [],
      select: (res) => {
        return extendDSPMetrics(res.data);
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeStats,
    },
  );

  const lineItemHistoryFilters = {
    date__gte: startDate,
    date__lte: moment(endDate).add(1, "day").format("YYYY-MM-DD"),
  };

  if (filters?.line_item_id) {
    lineItemHistoryFilters["amazon_line_item_id"] = filters.line_item_id;
  }

  const lineItemHistoryDimensions = dimensions.replace("line_item_id", "amazon_line_item_id");

  const { data: historyData } = useFetch(
    ["lineItemHistoryData", formattedFilters, dimensions],
    "/api/data_report_v2/dsplineitemhistory/stats/",
    {
      filters: formatDatamartFilters({ ...lineItemHistoryFilters, dsp_seller_info_id: dspAdvertiser?.id }),
      dimensions: lineItemHistoryDimensions,
    },
    {
      default: [],
      select: (res) => {
        return res.data;
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeMetadata,
    },
  );

  const mergedTrendData = useMemo(() => {
    const data = mergeTrendData(dspLineItemStatsData, prevPeriodDSPLineItemStatsData, "line_item_id");
    if (!historyData) {
      return data;
    }

    const historyIdx = historyData.reduce((acc, item) => {
      const key = item.date || item.amazon_line_item_id;
      acc[key] = item;
      return acc;
    }, {});
    for (const item of data) {
      const key = item.date || item.line_item_id;
      const historyItem = historyIdx[key];
      if (historyItem) {
        item.base_bid = historyItem.base_bid;
      }
    }
    return data;
  }, [dspLineItemStatsData, prevPeriodDSPLineItemStatsData, historyData]);

  const isLoading =
    isDSPLineItemStatsDataLoading ||
    isDSPLineItemEntityDataLoading ||
    isPrevPeriodDSPLineItemStatsDataLoading ||
    isDSPTargetDataLoading;

  return { data: mergedTrendData, metadata: dspLineItemEntityData, targetData: dspLineTargetData, isLoading };
};

export default useDSPLineItemData;
